/**
 * Product Tabs Component
 * 
 * Handles tab switching on all devices (horizontal tabs)
 * Also manages FAQ expand/collapse functionality
 * Handles Read More toggle with full desktop & mobile support
 * Detects overflow and shows scroll indicators
 */
class ProductTabs extends HTMLElement {
  constructor() {
    super();
    this.headers = [];
    this.panels = [];
    this.initialized = false;
    this.scrollTimeout = null;
    this.resizeTimeout = null;
  }

  connectedCallback() {
    this.refreshElements();
    this.tabsWrapper = this.closest('[data-product-tabs-wrapper]') || this.tabsWrapper;
    this.eventsController = new AbortController();
    this.applyDefaultTab();
    if (!this.initialized) {
      this.bindEvents();
      this.bindLangSwitcher();
      this.initialized = true;
    }

    this.handleReadMore();
    
    this.layoutFrame = requestAnimationFrame(() => {
      this.refreshElements();
      this.applyDefaultTab();
      this.handleDesktopRelocation();
      this.handleReadMore();
      this.checkScrollIndicators();
    });

    // Re-evaluate when all fonts/assets load
    if (document.readyState === 'complete') {
      this.handleReadMore();
    } else {
      window.addEventListener('load', () => {
        this.handleReadMore();
        this.checkScrollIndicators();
      }, { once: true, signal: this.eventsController.signal });
    }
  }

  disconnectedCallback() {
    this.eventsController?.abort();
    clearTimeout(this.scrollTimeout);
    clearTimeout(this.resizeTimeout);
    cancelAnimationFrame(this.layoutFrame);
    this.initialized = false;
  }

  applyDefaultTab() {
    const desktop = window.innerWidth >= 750;
    const available = this.headers.map(header => header.dataset.tab);
    if (!this.selectedTab || !available.includes(this.selectedTab) || (desktop !== this.desktopMode && !this.userSelectedTab)) {
      const preferred = desktop ? this.dataset.defaultDesktop : this.dataset.defaultMobile;
      this.switchTab(available.includes(preferred) ? preferred : available[0], { initial:true, scroll:false });
    }
    this.desktopMode = desktop;
    this.setAttribute('data-tabs-ready','');
  }

  refreshElements() {
    this.headers = Array.from(this.querySelectorAll('.product-tabs__header'));
    this.panels = Array.from(this.querySelectorAll('.product-tabs__panel'));
    this.headersWrapper = this.querySelector('.product-tabs__headers-wrapper');
    this.headersContainer = this.querySelector('.product-tabs__headers');
  }

  bindLangSwitcher() {
    this.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-lang-target]');
      if (!btn || !this.contains(btn)) return;
      
      e.preventDefault();
      const targetLang = btn.dataset.langTarget;
      const langBtns = Array.from(this.querySelectorAll('[data-lang-target]'));
      const langViews = Array.from(this.querySelectorAll('[data-lang-view]'));

      // Update active states on buttons
      langBtns.forEach(b => {
        const isActive = b.dataset.langTarget === targetLang;
        b.classList.toggle('product-tabs__lang-btn--active', isActive);
        b.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });

      // Update active states on language instruction views
      langViews.forEach(view => {
        const isActive = view.dataset.langView === targetLang;
        view.classList.toggle('product-tabs__lang-view--active', isActive);
      });
    }, { signal:this.eventsController.signal });
  }

  bindEvents() {
    // Native details grouping plus a fallback for browsers without name support.
    this.addEventListener('toggle', event => {
      const item = event.target;
      if (!item.matches?.('.product-tabs__faq-item') || !item.open) return;
      const group = item.parentElement;
      group.querySelectorAll('.product-tabs__faq-item[open]').forEach(other => {
        if (other !== item) other.open = false;
      });
    }, { capture:true, signal:this.eventsController.signal });

    // 1. Delegated click handling for tabs and read-more buttons
    this.addEventListener('click', (e) => {
      // Tab header clicked
      const header = e.target.closest('.product-tabs__header');
      if (header && this.contains(header)) {
        e.preventDefault();
        const tabId = header.dataset.tab;
        if (tabId) {
          this.switchTab(tabId);
        }
        return;
      }

      // Read More / Read Less button clicked
      const readMoreBtn = e.target.closest('[data-read-more]');
      if (readMoreBtn && this.contains(readMoreBtn)) {
        e.preventDefault();
        e.stopPropagation();
        this.toggleReadMore(readMoreBtn, e);
        return;
      }
    }, { signal:this.eventsController.signal });

    this.addEventListener('keydown', event => {
      const header = event.target.closest('.product-tabs__header');
      if (!header || !['ArrowLeft','ArrowRight','Home','End'].includes(event.key)) return;
      event.preventDefault();
      const rtl = getComputedStyle(this).direction === 'rtl';
      let index = this.headers.indexOf(header);
      if (event.key === 'Home') index = 0;
      else if (event.key === 'End') index = this.headers.length - 1;
      else index = (index + ((event.key === 'ArrowRight') !== rtl ? 1 : -1) + this.headers.length) % this.headers.length;
      const next = this.headers[index];
      this.switchTab(next.dataset.tab);
      next.focus({ preventScroll:true });
    }, { signal:this.eventsController.signal });

    // 2. Scroll event to update horizontal tab indicators
    if (this.headersContainer) {
      this.headersContainer.addEventListener('scroll', () => {
        clearTimeout(this.scrollTimeout);
        this.scrollTimeout = setTimeout(() => {
          this.checkScrollIndicators();
        }, 80);
      }, { passive: true, signal:this.eventsController.signal });
    }

    // 3. Window resize to recalculate relocation, indicators, and read more
    window.addEventListener('resize', () => {
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = setTimeout(() => {
        this.handleDesktopRelocation();
        this.applyDefaultTab();
        this.checkScrollIndicators();
        this.handleReadMore();
      }, 150);
    }, { signal:this.eventsController.signal });
  }

  toggleReadMore(button, event) {
    if (typeof window.toggleProductTabsReadMore === 'function') {
      window.toggleProductTabsReadMore(button, event);
      return;
    }

    const wrapper = button.closest('[data-description-wrapper]');
    if (!wrapper) return;
    
    const content = wrapper.querySelector('[data-description-content]');
    if (!content) return;
    
    const isCurrentlyExpanded = button.classList.contains('expanded') || content.classList.contains('expanded');
    const willExpand = !isCurrentlyExpanded;
    const textSpan = button.querySelector('.product-tabs__read-more-text');
    const lessSpan = button.querySelector('.product-tabs__read-less-text');
    const icon = button.querySelector('.product-tabs__read-more-icon');
    const isMobile = window.innerWidth < 750;
    const clampLimit = isMobile ? 110 : 120;

    button.classList.toggle('expanded', willExpand);
    content.classList.toggle('expanded', willExpand);
    button.setAttribute('aria-expanded', willExpand ? 'true' : 'false');

    if (willExpand) {
      content.style.setProperty('max-height', 'none', 'important');
      content.style.setProperty('overflow', 'visible', 'important');
      if (textSpan) textSpan.style.setProperty('display', 'none', 'important');
      if (lessSpan) lessSpan.style.setProperty('display', 'inline', 'important');
      if (icon) icon.style.setProperty('transform', 'rotate(180deg)', 'important');
    } else {
      content.style.setProperty('max-height', clampLimit + 'px', 'important');
      content.style.setProperty('overflow', 'hidden', 'important');
      if (textSpan) textSpan.style.setProperty('display', 'inline', 'important');
      if (lessSpan) lessSpan.style.setProperty('display', 'none', 'important');
      if (icon) icon.style.setProperty('transform', 'none', 'important');

      const rect = wrapper.getBoundingClientRect();
      if (rect.top < 0) {
        wrapper.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'start' });
      }
    }
  }

  handleDesktopRelocation() {
    const wrapper = this.tabsWrapper || this.closest('[data-product-tabs-wrapper]');
    if (!wrapper) return;
    
    const placement = wrapper.getAttribute('data-desktop-placement') || 'full_width_below';
    if (placement === 'inline') return;

    const mobilePlaceholder = wrapper.querySelector('[data-tabs-mobile-placeholder]');
    const section = wrapper.closest('[id^="shopify-section-"]') || wrapper.closest('.product-information') || document;
    const mediaContainer = section.querySelector('[data-media-tabs-desktop]');
    const fullContainer = section.querySelector('[data-full-tabs-desktop]');
    
    const isDesktop = window.innerWidth >= 750;

    if (isDesktop) {
      let target = null;
      if (placement === 'below_image') {
        target = mediaContainer;
      } else if (placement === 'full_width_below') {
        target = fullContainer;
      }

      if (target && this.parentElement !== target) {
        target.appendChild(this);
        this.classList.add('product-tabs--relocated-desktop');
      }
    } else {
      // Mobile: ensure tabs reside in original product details stack
      if (mobilePlaceholder && this.parentElement !== wrapper) {
        mobilePlaceholder.after(this);
        this.classList.remove('product-tabs--relocated-desktop');
      }
    }
  }

  switchTab(tabId, { initial = false, scroll = true } = {}) {
    this.refreshElements();
    if (!this.headers.some(header => header.dataset.tab === tabId)) return;
    this.selectedTab = tabId;
    if (!initial) this.userSelectedTab = true;

    // Update headers
    this.headers.forEach(header => {
      const isActive = header.dataset.tab === tabId;
      header.classList.toggle('product-tabs__header--active', isActive);
      header.setAttribute('aria-selected', isActive ? 'true' : 'false');
      header.tabIndex = isActive ? 0 : -1;
    });

    // Update panels
    this.panels.forEach(panel => {
      const isActive = panel.dataset.panel === tabId;
      panel.classList.toggle('product-tabs__panel--active', isActive);
      panel.hidden = !isActive;
      panel.tabIndex = 0;
    });

    // Re-check Read More when switching tabs
    this.handleReadMore();

    // Close FAQ items when switching away from FAQ
    if (tabId !== 'faq') {
      const faqItems = this.querySelectorAll('.product-tabs__faq-item[open]');
      faqItems.forEach(item => {
        item.removeAttribute('open');
      });
    }

    // Scroll active tab into view if partially hidden
    const activeHeader = this.querySelector('.product-tabs__header--active');
    if (scroll && activeHeader && this.headersContainer) {
      const containerRect = this.headersContainer.getBoundingClientRect();
      const headerRect = activeHeader.getBoundingClientRect();
      
      if (headerRect.left < containerRect.left || headerRect.right > containerRect.right) {
        activeHeader.scrollIntoView({
          behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
          block: 'nearest',
          inline: 'center'
        });
      }
    }

    // Scroll to tabs on mobile if needed
    if (scroll && window.innerWidth < 750) {
      const headers = this.querySelector('.product-tabs__headers');
      if (headers) {
        const headerRect = headers.getBoundingClientRect();
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const targetPosition = headerRect.top + scrollTop - 20;
        
        if (headerRect.top < 0 || headerRect.bottom > window.innerHeight) {
          window.scrollTo({
            top: targetPosition,
            behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'
          });
        }
      }
    }
  }

  handleReadMore() {
    const buttons = Array.from(this.querySelectorAll('[data-read-more]'));
    if (buttons.length === 0) return;

    const isMobile = window.innerWidth < 750;
    const clampLimit = isMobile ? 110 : 120;

    buttons.forEach(button => {
      const wrapper = button.closest('[data-description-wrapper]');
      if (!wrapper) return;
      
      const content = wrapper.querySelector('[data-description-content]');
      if (!content) return;
      
      // If currently expanded, preserve state
      if (button.classList.contains('expanded') || content.classList.contains('expanded')) {
        button.style.display = 'inline-flex';
        content.style.setProperty('max-height', 'none', 'important');
        content.style.setProperty('overflow', 'visible', 'important');
        return;
      }
      
      // Accurately measure unconstrained content height
      const currentMaxHeight = content.style.maxHeight;
      content.style.maxHeight = 'none';
      const actualHeight = content.scrollHeight;
      content.style.maxHeight = currentMaxHeight;
      
      // If content overflows clamp limit by at least 15px (more than 1 line overflow), show button
      if (actualHeight > (clampLimit + 15)) {
        button.style.display = 'inline-flex';
        content.style.setProperty('max-height', clampLimit + 'px', 'important');
        content.style.setProperty('overflow', 'hidden', 'important');
      } else {
        // Content fits completely; hide read more button
        button.style.display = 'none';
        content.style.setProperty('max-height', 'none', 'important');
        content.style.setProperty('overflow', 'visible', 'important');
      }
    });
  }

  checkScrollIndicators() {
    if (!this.headersContainer || !this.headersWrapper) return;
    
    const container = this.headersContainer;
    const wrapper = this.headersWrapper;
    
    const scrollLeft = container.scrollLeft;
    const maxScroll = container.scrollWidth - container.clientWidth;
    
    const showLeft = scrollLeft > 5;
    const showRight = scrollLeft < maxScroll - 5;
    
    wrapper.classList.toggle('show-left', showLeft);
    wrapper.classList.toggle('show-right', showRight);
  }

  // Public method to open a specific tab
  openTab(tabId) {
    this.switchTab(tabId);
  }
}

// Register the custom element
if (!customElements.get('product-tabs')) {
  customElements.define('product-tabs', ProductTabs);
}
